import { useState, useEffect } from 'react';
import AppLayout from '@/components/feature/AppLayout';

const getRisk = (c: any) => {

  let score = 75;

  switch (c.incident_type) {

    case 'Sexual Abuse':
    case 'Child Trafficking':
    case 'Online Sexual Exploitation':
      score = 95;
      break;

    case 'Physical Abuse':
      score = 90;
      break;

    case 'Emotional Abuse':
      score = 80;
      break;

    case 'Neglect':
    case 'Child Labor':
    case 'Abandonment':
      score = 75;
      break;

    default:
      score = 70;

  }

  if (c.prior_incidents === 'yes')
    score += 5;

  if (c.priority_level === 'urgent')
    score += 3;

  if (c.priority_level === 'emergency')
    score += 5;

  if (score > 100)
    score = 100;

  let level = 'low';

  if (score >= 90)
    level = 'critical';

  else if (score >= 80)
    level = 'high';

  else if (score >= 75)
    level = 'moderate';

  return {

    score,
    level

  };

};

const riskColor = (risk: string) => {

  return risk === 'critical'
    ? 'bg-red-100 text-red-700 border-red-200'
    : risk === 'high'
    ? 'bg-orange-100 text-orange-700 border-orange-200'
    : risk === 'moderate'
    ? 'bg-amber-100 text-amber-700 border-amber-200'
    : 'bg-green-100 text-green-700 border-green-200';

};

const getAIRecommendation = (c: any) => {

  const risk = getRisk(c);

  let recommendation = '';

  switch (c.incident_type) {

    case 'Sexual Abuse':

      recommendation =
        'Immediate trauma-focused intervention, emergency child protection response, medico-legal examination, and psychological counseling are strongly recommended. Coordinate with law enforcement and DSWD Child Protection Unit immediately.';

      break;

    case 'Child Trafficking':

      recommendation =
        'Activate anti-trafficking protocols immediately. Recommend emergency rescue coordination, witness protection, legal intervention, and intensive psychosocial recovery services.';

      break;

    case 'Online Sexual Exploitation':

      recommendation =
        'Recommend cybercrime investigation referral, digital evidence preservation, immediate psychological assessment, and emergency protective custody if necessary.';

      break;

    case 'Physical Abuse':

      recommendation =
        'Recommend medical examination, barangay protection intervention, family risk assessment, and mandatory monitoring by assigned social worker.';

      break;

    case 'Emotional Abuse':

      recommendation =
        'Recommend psychological evaluation, behavioral monitoring, guided counseling sessions, and family mediation support services.';

      break;

    case 'Neglect':

      recommendation =
        'Recommend family assessment, livelihood intervention, educational monitoring, and parental capability evaluation.';

      break;

    case 'Child Labor':

      recommendation =
        'Recommend child labor investigation, school reintegration assistance, livelihood support for guardians, and continuous monitoring.';

      break;

    case 'Abandonment':

      recommendation =
        'Recommend temporary shelter assessment, legal custody review, psychosocial support, and family tracing procedures.';

      break;

    default:

      recommendation =
        'Recommend detailed case assessment, risk monitoring, and supervisor review for proper intervention planning.';

  }

  return {

    riskLevel: risk.level,

    riskScore: risk.score,

    recommendation,

    action:

      risk.level === 'critical'
        ? 'Immediate Intervention Required'

      : risk.level === 'high'
        ? 'Urgent Supervisor Review'

      : risk.level === 'moderate'
        ? 'Scheduled Social Intervention'

      : 'Routine Monitoring'

  };

};

export default function SupDecisionSupportPage() {
  const [decisionOverrides, setDecisionOverrides] = useState<Record<string, string>>({});
  const [overrideInput, setOverrideInput] = useState<string | null>(null);
  const [overrideText, setOverrideText] = useState('');
  const [activeCases, setActiveCases] = useState<any[]>([]);
  const [user, setUser] = useState<any>(null);
  const [aiData, setAiData] = useState<any>({});
  const [savedRecommendations, setSavedRecommendations] = useState<any[]>([]);

  useEffect(() => {

    const authUser =
      JSON.parse(
        localStorage.getItem('user') || '{}'
      );

    setUser(authUser);

    fetch(
      'https://guardiancloudcare.com/backend/api/get_escalated_cases.php'
    )
    .then(res => res.json())
    .then(data => {

      if (Array.isArray(data)) {

        setActiveCases(data);

      }

    });

    activeCases.forEach(async (c) => {

      const ai = await getAIRecommendation(c);

      setAiData((prev: any) => ({

        ...prev,

        [c.case_number]: ai

      }));

    });

    fetch(
      'https://guardiancloudcare.com/backend/api/get_ai_recommendations.php'
    )
    .then(res => res.json())
    .then(data => {

      if (Array.isArray(data)) {

        setSavedRecommendations(data);

      }

    });

  }, []);

  return (
    <AppLayout title="Decision Support" breadcrumb={['Supervisor', 'Decision Support']}>
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-5 text-white flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0"><i className="ri-lightbulb-line text-2xl" /></div>
          <div>
            <h3 className="font-bold text-lg">AI Decision Support</h3>
            <p className="text-amber-100 text-sm">System suggests priority levels and recommended actions. You can confirm or override each suggestion.</p>
          </div>
        </div>

        {activeCases.map((c) => {
          const risk = getRisk(c);
          const ai = getAIRecommendation(c);
          const saved =
          savedRecommendations.find(
            (r) =>
              r.case_number === c.case_number
          );
          const override = decisionOverrides[c.id];

          return (
            <div key={c.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">{c.first_name} {c.last_name} — <span className="font-mono text-xs text-gray-500">{c.case_number}</span></p>
                  <p className="text-xs text-gray-500">{c.incident_type} · {c.barangay}</p>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full border font-medium capitalize ${
                    riskColor(risk.level)
                  }`}
                >

                  {risk.level} ({risk.score})

                </span>
              </div>
              <div className="px-5 py-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1"><i className="ri-lightbulb-line" />AI Suggested Priority:</p>
                  <p className="text-sm font-bold text-amber-800">{ai.action}</p>
                </div>
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Recommended Actions:</p>
                  <ul className="space-y-1">
                    <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">

                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">

                        AI Recommendation

                      </p>

                      <p className="text-sm text-gray-700 leading-relaxed">

                        {ai.recommendation}

                      </p>

                    </div>
                  </ul>
                </div>
                {(override || saved) && (
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-3">
                    <p className="text-xs font-semibold text-teal-700 mb-1">Supervisor Decision:</p>
                    <p className="text-sm text-teal-800">{override || saved?.recommendation}</p>
                  </div>
                )}
                {overrideInput === c.id && (
                  <div className="mb-3">
                    <textarea value={overrideText} onChange={(e) => setOverrideText(e.target.value)} rows={2} maxLength={300} placeholder="Enter override note..." className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-teal-500 resize-none mb-2" />
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          const recommendation =
                            `Override Applied — ${overrideText}`;
                          setDecisionOverrides((prev) => ({
                            ...prev,
                            [c.id]: recommendation
                          }));
                          await fetch(
                            'https://guardiancloudcare.com/backend/api/save_ai_recommendation.php',
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({
                                uid: user?.uid,
                                case_number:
                                  c.case_number,
                                incident_type:
                                  c.incident_type,
                                risk_level:
                                  risk.level,
                                recommendation
                              })
                            }
                          );
                          setOverrideInput(null);
                          setOverrideText('');
                        }} className="text-sm bg-teal-500 text-white px-4 py-1.5 rounded-lg hover:bg-teal-600 cursor-pointer whitespace-nowrap">Save Override</button>
                      <button onClick={() => { setOverrideInput(null); setOverrideText(''); }} className="text-sm border border-gray-200 px-4 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancel</button>
                    </div>
                  </div>
                )}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={async () => {
                      const recommendation =
                        `Confirmed — ${ai.action}`;
                      setDecisionOverrides((prev) => ({
                        ...prev,
                        [c.id]: recommendation
                      }));

                      await fetch(
                        'https://guardiancloudcare.com/backend/api/save_ai_recommendation.php',
                        {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            uid: user?.uid,
                            case_number:
                              c.case_number,
                            incident_type:
                              c.incident_type,
                            risk_level:
                              risk.level,
                            recommendation
                          })
                        }
                      );
                    }}
                    className="text-sm bg-teal-500 text-white px-4 py-2 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap flex items-center gap-1">
                    <i className="ri-checkbox-circle-line" />Confirm
                  </button>
                  <button onClick={() => { setOverrideInput(c.id); setOverrideText(''); }}
                    className="text-sm border border-gray-200 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-1">
                    <i className="ri-edit-line" />Override
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </AppLayout>
  );
}
